import { Internationalization } from '@syncfusion/ej2-base';
import { Typography } from '@getsynapse/design-system';
import { ProjectResource } from 'utils/types/learningSchedule';
import { Link } from 'react-router-dom';

export const getDateTime = (
  startTime: Date,
  endTime: Date,
  isAllDay: boolean,
  isOldEvent: boolean
): string => {
  const intl = new Internationalization();
  if (isOldEvent) {
    if (isAllDay) {
      const isSingleDayAllDay = isSingleDayAllDayEvent(startTime, endTime);
      if (isSingleDayAllDay) {
        return (
          intl.formatDate(startTime, { type: 'date', skeleton: 'full' }) +
          ' (All Day)'
        );
      } else {
        const displayEnd = new Date(endTime);
        displayEnd.setUTCDate(displayEnd.getUTCDate() - 1);
        return (
          intl.formatDate(startTime, { type: 'date', skeleton: 'full' }) +
          ' - ' +
          intl.formatDate(displayEnd, { type: 'date', skeleton: 'full' }) +
          ' (All Day)'
        );
      }
    }
  } else {
    if (isAllDay) {
      // Check if it's a multi-day all-day event
      const isMultiDay = startTime.toDateString() !== endTime.toDateString();

      if (isMultiDay) {
        // For multi-day all-day events, show the date range
        // Adjust end date to show the actual end date (not exclusive)
        const actualEndDate = new Date(endTime);
        actualEndDate.setDate(actualEndDate.getDate() - 1);

        return (
          intl.formatDate(startTime, { type: 'date', skeleton: 'full' }) +
          ' - ' +
          intl.formatDate(actualEndDate, { type: 'date', skeleton: 'full' }) +
          ' (All Day)'
        );
      } else {
        // For single-day all-day events, show just the start date
        return (
          intl.formatDate(startTime, { type: 'date', skeleton: 'full' }) +
          ' (All Day)'
        );
      }
    }
  }

  // sameDay timed event detection
  const isEndAtMidnight =
    endTime.getHours() === 0 &&
    endTime.getMinutes() === 0 &&
    endTime.getSeconds() === 0 &&
    endTime.getMilliseconds() === 0;
  const adjustedEnd = isEndAtMidnight
    ? new Date(endTime.getTime() - 1)
    : endTime;
  const sameDay =
    startTime.getFullYear() === adjustedEnd.getFullYear() &&
    startTime.getMonth() === adjustedEnd.getMonth() &&
    startTime.getDate() === adjustedEnd.getDate();

  if (!isAllDay && sameDay) {
    return (
      intl.formatDate(startTime, { type: 'date', skeleton: 'full' }) +
      ' (' +
      intl.formatDate(startTime, { skeleton: 'hm' }) +
      ' - ' +
      intl.formatDate(endTime, { skeleton: 'hm' }) +
      ')'
    );
  } else {
    return (
      intl.formatDate(startTime, { type: 'date', skeleton: 'full' }) +
      ' (' +
      intl.formatDate(startTime, { skeleton: 'hm' }) +
      ') - ' +
      intl.formatDate(endTime, { type: 'date', skeleton: 'full' }) +
      ' (' +
      intl.formatDate(endTime, { skeleton: 'hm' }) +
      ')'
    );
  }
};

function isSingleDayAllDayEvent(start: Date, end: Date): boolean {
  // Check if duration is exactly 24 hours
  const duration = end.getTime() - start.getTime();
  if (duration !== 24 * 60 * 60 * 1000) {
    return false;
  }

  // For all-day events, if they span exactly 24 hours, they should be treated as single-day
  // regardless of the timezone they were created in
  return true;
}

export const renderProjectNameLink = ({
  projectElement,
  index = 0,
  dataTestId,
}: {
  projectElement: ProjectResource;
  index: number;
  dataTestId: string;
}) => {
  return (
    <Link
      rel='noreferrer'
      target='_blank'
      key={index}
      to={`/project/${projectElement.id}`}
      data-testid={`${dataTestId}-${index}`}
    >
      <Typography
        key={index}
        variant='body2'
        className='underline text-secondary line-clamp-1'
      >
        {projectElement.title}
      </Typography>
    </Link>
  );
};

export const oldGetDateTime = (
  startTime: Date,
  endTime: Date,
  isAllDay: boolean
) => {
  const intl = new Internationalization();
  const isSameDay = oldIsSameDayEvent(
    startTime.toISOString(),
    endTime.toISOString()
  );
  const adjustedStartTime = startTime;
  let adjustedEndTime = endTime;

  if (isAllDay && !isSameDay) {
    adjustedEndTime = new Date(endTime);
    adjustedEndTime.setDate(adjustedEndTime.getDate() - 1);
  }
  if (isAllDay) {
    if (isSameDay) {
      return (
        intl.formatDate(adjustedStartTime, { type: 'date', skeleton: 'full' }) +
        ' (All Day)'
      );
    } else {
      return (
        intl.formatDate(adjustedStartTime, { type: 'date', skeleton: 'full' }) +
        ' - ' +
        intl.formatDate(adjustedEndTime, { type: 'date', skeleton: 'full' }) +
        ' (All Day)'
      );
    }
  } else {
    if (isSameDay) {
      return (
        intl.formatDate(startTime, { type: 'date', skeleton: 'full' }) +
        ' (' +
        intl.formatDate(startTime, { skeleton: 'hm' }) +
        ' - ' +
        intl.formatDate(endTime, { skeleton: 'hm' }) +
        ')'
      );
    } else {
      return (
        intl.formatDate(startTime, { type: 'date', skeleton: 'full' }) +
        ' (' +
        intl.formatDate(startTime, { skeleton: 'hm' }) +
        ')' +
        ' - ' +
        intl.formatDate(endTime, { type: 'date', skeleton: 'full' }) +
        ' (' +
        intl.formatDate(endTime, { skeleton: 'hm' }) +
        ')'
      );
    }
  }
};

const oldIsSameDayEvent = (
  startTimeStr: string,
  endTimeStr: string
): boolean => {
  const startTime = new Date(startTimeStr);
  const endTime = new Date(endTimeStr);
  const startDate = new Date(
    startTime.getFullYear(),
    startTime.getMonth(),
    startTime.getDate()
  );
  const endDate = new Date(
    endTime.getFullYear(),
    endTime.getMonth(),
    endTime.getDate()
  );
  // Check if endTime is midnight (00:00:00)
  if (
    endTime.getHours() === 0 &&
    endTime.getMinutes() === 0 &&
    endTime.getSeconds() === 0
  ) {
    endDate.setDate(endDate.getDate() - 1);
  }
  return startDate.getTime() === endDate.getTime();
};
